"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { useState } from "react";

const inputClass =
  "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#000643] focus:outline-none";

export default function LoginForm(): JSX.Element {
  const router = useRouter();
  const params = useSearchParams();
  const callbackUrl = params?.get("callbackUrl") || "/rooms";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const res = await signIn("credentials", { email, password, redirect: false, callbackUrl });
    setLoading(false);
    if (!res || res.error) {
      setError("Incorrect email or password.");
      return;
    }
    router.push(res.url || callbackUrl);
    router.refresh();
  }

  return (
    <div className="mx-auto max-w-md">
      <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        <h1 className="font-bold text-2xl text-[#000643]">Sign in</h1>
        <p className="mt-1 text-gray-600 text-sm">Access your NATO Edge 26 meeting-room bookings.</p>

        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
          <label className="block">
            <span className="font-medium text-gray-700 text-sm">Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="font-medium text-gray-700 text-sm">Password</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {error ? <p className="rounded-lg bg-red-50 px-3 py-2 text-red-700 text-sm">{error}</p> : null}

          {/* Cal's own reset flow, which existed all along with no way in from
              here — so a forgotten password meant editing the database. */}
          <p className="text-right">
            <Link href="/auth/forgot-password" className="text-gray-500 text-xs hover:text-[#000643]">
              Forgot your password?
            </Link>
          </p>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-[#000643] px-4 py-2.5 font-semibold text-sm text-white transition hover:opacity-90 disabled:opacity-40">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-5 text-center text-gray-600 text-sm">
          No account yet?{" "}
          <Link
            href={`/rooms/signup?callbackUrl=${encodeURIComponent(callbackUrl)}`}
            className="font-semibold text-[#000643] hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  );
}
